import { Component, OnInit } from '@angular/core';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { Currency, ExchangeRate } from './models/currencies.model';
import { CurrenciesService } from './services/currencies.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { defer, forkJoin } from 'rxjs';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { api_routes } from '@app/consts/api-routes';
import { db_tables } from '@app/consts';
import { RowActionButtonInput } from '../talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { SortDescriptor } from '@progress/kendo-data-query';

@Component({
  selector: 'app-currencies',
  templateUrl: './currencies.component.html',
  styleUrls: ['./currencies.component.scss']
})
export class CurrenciesComponent implements OnInit {
  public columns: any[] = [
    { field: "from", subField: "id", title: "From", localizationCode: "ExchangeRate-From" },
    { field: "to", subField: "id", title: "To", localizationCode: "ExchangeRate-To" },
    { field: "asOf", title: "As Of", type: "date", tableId: "tbl_BsPlanDescription" },
    { field: "rate", title: "Rate", tableId: "tbl_BsPlanName", },
    { field: "version", subField: "createdBy", title: "Created By", type: "employee", localizationCode: "CreatedBy" },
    { field: "version", subField: "createdOn", title: "Created On", type: "dateTime", localizationCode: "CreatedOn" },
  ];

  filterCategories: any[] = [
    { field: "rate", title: "Rate", tableId: "tbl_BsPlanName", type: "Number", dataType: 'Number' },
    { field: "from.id", title: "From", tableId: "tbl_BsPlanName", dataType: "SpecialLookup", lookupCode: 'CURRENCY' },
    { field: "to.id", title: "To", tableId: "tbl_BsPlanName", dataType: "SpecialLookup", lookupCode: 'CURRENCY' },
  ];

  sortableColumns: any[] = [
    { field: "rate", sortValue: "rate" },
    { field: "asOf", sortValue: "asOf" },
    { field: "from", sortValue: "from.id" },
    { field: "to", sortValue: "to.id" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;

  isLoadingCurrencies: boolean = false;
  isLoadingExchangeRates: boolean = false;
  isSavingExchangeRate: boolean = false;

  currencies: Currency[] = [];
  selectedCurrency: Currency = null;
  exchangeRates: ExchangeRate[] = [];
  currencySearchValue: string = '';

  togglingCurrencyIds: Set<string> = new Set<string>();
  getRequest: any;
  sort: SortDescriptor[] = [
      {
          field: 'from',
          dir: 'asc'
      },
      {
          field: 'to',
          dir: 'asc'
      },
      {
          field: 'asOf',
          dir: 'desc'
      }
  ];
  sortString: string = "from.id-asc~to.id-asc~asOf-desc";
  filterString: string;

  exchangeRateFormId: string = 'frm_jF4l9PVywPGOiq';
  editExchangeRateFormId: string = 'frm_672fj3qUm8ZY7M';
  exchangeRateChangeReasonFormId: string = 'frm_elArH1uvUZ0WcM';
  dialogRef: any;

  gridId = 'currenciesExchangeRatesGrid';
  tableId = db_tables.ExchangeRates;

  constructor(
    private currenciesService: CurrenciesService,
    private snackbarService: SnackbarService,
    public translate: TranslateService,
    private dialog: MatDialog,
    private overlayService: OverlayService
  ) { }

  ngOnInit(): void {
    this.getCurrencies();
  }

  get filteredCurrencies(): Currency[] {
    const searchValue = (this.currencySearchValue || '').trim().toLowerCase();

    if (!searchValue) {
      return this.currencies;
    }

    return this.currencies.filter(currency =>
      currency.id.toLowerCase().includes(searchValue)
    );
  }

  selectCurrency(currency: Currency): void {
    this.selectedCurrency = currency;

    this.getExchangeRates();
  }

  editOrCreateExchangeRate(exchangeRate: ExchangeRate): void {
    let formData = {
      id: exchangeRate?.id,
      rate: exchangeRate?.rate,
      asOf: exchangeRate?.asOf,
      from: exchangeRate?.from?.id || this.selectedCurrency.id,
      to: exchangeRate?.to?.id
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: exchangeRate ? this.editExchangeRateFormId : this.exchangeRateFormId,
      changeReasonFormId: this.exchangeRateChangeReasonFormId,
      formData: formData,
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((formData) => {
      if (exchangeRate) {
        this.updateExchangeRate(exchangeRate.id, formData);
      } else {
        this.createExchangeRate(formData);
      }
      this.dialogRef.close();
      sub.unsubscribe();
    });
  }

  updateExchangeRate(id: string, formData: any) {
    this.overlayService.show();

    this.currenciesService.updateExchangeRate(id, formData)
      .subscribe(
        {
          next: (v) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('UpdatedSuccessfully')}`, 'clear', 'success');
            this.getExchangeRates();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  createExchangeRate(formData: any) {
    this.overlayService.show();

    this.currenciesService.createExchangeRate(formData)
      .subscribe(
        {
          next: (v) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
            this.getExchangeRates();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  openConfirmDeleteDialog(importIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteExchangeRates(importIds);
        }
      }
    );
  }

  deleteExchangeRates(exchangeRateIds: string[]) {
    const observables = exchangeRateIds.map(selectedItem => defer(() => this.currenciesService.deleteExchangeRate(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getExchangeRates();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.getRequest) {
      this.getRequest.unsubscribe();
    }
    this.getExchangeRates();
  }

  toggleCurrency(currency: Currency, isEnabled: boolean): void {
    if (!currency || this.togglingCurrencyIds.has(currency.id)) {
      return;
    }

    this.togglingCurrencyIds.add(currency.id);

    const payload = {
      changeReason: null,
      changeReasonComments: null,
      originalCreatedOn: null,
      asOf: new Date().toISOString(),
      isEnabled
    };

    this.currenciesService.enableDisableCurrency(currency.id, payload)
      .pipe(finalize(() => this.togglingCurrencyIds.delete(currency.id)))
      .subscribe(
        () => {
          currency.isEnabled = isEnabled;

          if (this.selectedCurrency?.id === currency.id) {
            this.selectedCurrency.isEnabled = isEnabled;
          }

          this.snackbarService.openSnackBar(
            isEnabled ? 'Currency enabled.' : 'Currency disabled.',
            'clear',
            'success'
          );
        },
        (error) => {
          this.snackbarService.openSnackBar(error, 'clear', 'warn');
        }
      );
  }

  private getCurrencies(): void {
    this.isLoadingCurrencies = true;

    this.currenciesService.getCurrencies(this.filterString, this.sortString)
      .pipe(finalize(() => this.isLoadingCurrencies = false))
      .subscribe(
        (currencies) => {
          this.currencies = currencies || [];

          if (this.currencies.length > 0) {
            this.selectCurrency(this.currencies[0]);
          }
        },
        (error) => {
          this.snackbarService.openSnackBar(error, 'clear', 'warn');
        }
      );
  }

  private getExchangeRates(): void {
    if (!this.selectedCurrency?.id) {
      this.gridDataResult = {
        data: [],
        total: 0
      };
      return;
    }

    this.isLoadingExchangeRates = true;

    this.getRequest = this.currenciesService.getExchangeRates(this.selectedCurrency.id, this.pageSize, this.skip, this.filterString, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };

            this.isLoadingExchangeRates = false;
          },
          error: (e) => {
            this.isLoadingExchangeRates = false;
            this.overlayService.hide();
          },
          complete: () => {
            this.isLoadingExchangeRates = false;
            this.overlayService.hide();
          } 
        }
      );
  }

}
